import { useState, useEffect } from "react";
import "@/App.css";
import "@/styles/mobile.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { config } from '@/config/wagmi';
import { DynamicContextProvider } from '@dynamic-labs/sdk-react-core';
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import SettingsPage from "@/pages/SettingsPage";
import EnhancedChatInterface from "@/pages/EnhancedChatInterface";
import EnhancedTransferPage from "@/pages/EnhancedTransferPage";
import ModernTransferPage from "@/pages/ModernTransferPage";
import CryptoPaymentPage from "@/pages/CryptoPaymentPage";
import CryptoTransferPage from "@/pages/CryptoTransferPage";
import NonCustodialWalletDashboard from "@/pages/NonCustodialWalletDashboard";
import NonCustodialSendPage from "@/pages/NonCustodialSendPage";
import NonCustodialReceivePage from "@/pages/NonCustodialReceivePage";
import NonCustodialHistoryPage from "@/pages/NonCustodialHistoryPage";
import OnboardingTutorial from "@/pages/OnboardingTutorial";
import ContactListPage from "@/pages/ContactListPage";
import CryptoReceivePage from "@/pages/CryptoReceivePage";
import CryptoHistoryPage from "@/pages/CryptoHistoryPage";
import CryptoUsernameSetup from "@/pages/CryptoUsernameSetup";
import CryptoSettingsPage from "@/pages/CryptoSettingsPage";
import TransactionStatusPage from "@/pages/TransactionStatusPage";
import CryptoAnalyticsPage from "@/pages/CryptoAnalyticsPage";
import TestnetSetupPage from "@/pages/TestnetSetupPage";
import FundingGuidePage from "@/pages/FundingGuidePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PhoneVerificationPage from "@/pages/PhoneVerificationPage";
import RecipientsPage from "@/pages/RecipientsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminInbox from "@/pages/AdminInbox";
import SimpleInbox from "@/pages/SimpleInbox";
import SimpleAdminDashboard from "@/pages/SimpleAdminDashboard";
import HelpCenter from "@/pages/HelpCenter";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ContactPage from "@/pages/ContactPage";
import KYCVerificationPage from "@/pages/KYCVerificationPage";
import VoiceAssistantPage from "@/pages/VoiceAssistantPage";
import SendMoneyPage from "@/pages/SendMoneyPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelledPage from "@/pages/PaymentCancelledPage";
import TransactionHistoryPage from "@/pages/TransactionHistoryPage";
import SupportedCountriesPage from "@/pages/SupportedCountriesPage";
import ClaimPage from "@/pages/ClaimPage";
import MTCNClaimPage from "@/pages/MTCNClaimPage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import FiatCurrenciesPage from "@/pages/FiatCurrenciesPage";
import FeesPage from "@/pages/FeesPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { WalletProvider } from "@/contexts/WalletContext";

// Initialize React Query client
const queryClient = new QueryClient();

// Dynamic.xyz Environment ID for embedded wallets
const DYNAMIC_ENVIRONMENT_ID = process.env.REACT_APP_DYNAMIC_ENVIRONMENT_ID || 'a9b45970-f2cf-480a-be2f-4abd81a6e333';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('routeflow_token');
    const userData = localStorage.getItem('routeflow_user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        
        // Validate user ID format (must be valid UUID)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        
        if (parsedUser.id && uuidRegex.test(parsedUser.id)) {
          setUser(parsedUser);
        } else {
          // Corrupted user data - clear it
          console.error('Corrupted user ID detected, clearing session');
          localStorage.removeItem('routeflow_token');
          localStorage.removeItem('routeflow_user');
        }
      } catch (error) {
        // Invalid JSON - clear it
        console.error('Invalid user data in localStorage, clearing session');
        localStorage.removeItem('routeflow_token');
        localStorage.removeItem('routeflow_user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    // Validate user data before storing
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (!userData || !userData.id || !uuidRegex.test(userData.id)) {
      console.error('Invalid user data received from login:', userData);
      toast.error('Login error: Invalid user data. Please try again.');
      return;
    }
    
    localStorage.setItem('routeflow_token', token);
    localStorage.setItem('routeflow_user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('routeflow_token');
    localStorage.removeItem('routeflow_user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <DynamicContextProvider
      settings={{
        environmentId: DYNAMIC_ENVIRONMENT_ID,
      }}
    >
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <WalletProvider>
          <div className="App">
            <BrowserRouter>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/settings" element={user ? <SettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto/setup-username" element={user ? <CryptoUsernameSetup user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto/settings" element={user ? <CryptoSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto/tx/:txHash" element={user ? <TransactionStatusPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto/analytics" element={user ? <CryptoAnalyticsPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/testnet-setup" element={user ? <TestnetSetupPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/funding-guide" element={<FundingGuidePage user={user} onLogout={handleLogout} />} />
            <Route path="/phone-verification" element={user ? <PhoneVerificationPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/chat" element={user ? <EnhancedChatInterface user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/transfer" element={user ? <ModernTransferPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/transfer-old" element={user ? <EnhancedTransferPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto-payment" element={user ? <CryptoPaymentPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/crypto-transfer" element={user ? <CryptoTransferPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/recipients" element={user ? <RecipientsPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/transactions" element={user ? <TransactionsPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/profile" element={user ? <ProfilePage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/admin" element={user && user.is_admin ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/admin/inbox" element={user && user.is_admin ? <AdminInbox user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/inbox" element={<SimpleInbox />} />
            <Route path="/dashboard-admin" element={<SimpleAdminDashboard />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/kyc" element={user ? <KYCVerificationPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/voice-assistant" element={user ? <VoiceAssistantPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/send-money" element={user ? <SendMoneyPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/send" element={user ? <SendMoneyPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/payment-success" element={<PaymentSuccessPage user={user} onLogout={handleLogout} />} />
            <Route path="/payment-cancelled" element={<PaymentCancelledPage user={user} onLogout={handleLogout} />} />
            <Route path="/transaction-history" element={user ? <TransactionHistoryPage user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            <Route path="/supported-countries" element={<SupportedCountriesPage user={user} onLogout={handleLogout} />} />
            <Route path="/payment-methods" element={<PaymentMethodsPage user={user} onLogout={handleLogout} />} />
            <Route path="/fiat-currencies" element={<FiatCurrenciesPage user={user} onLogout={handleLogout} />} />
            <Route path="/fees" element={<FeesPage />} />
            {/* Claim Pages - Public, no auth required */}
            <Route path="/claim" element={<ClaimPage />} />
            <Route path="/claim-mtcn" element={<MTCNClaimPage />} />
          </Routes>
          
          {/* Mobile Bottom Navigation - Only shows on mobile when logged in */}
          <MobileBottomNav user={user} />
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
          
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </div>
          </WalletProvider>
      </QueryClientProvider>
    </WagmiProvider>
    </DynamicContextProvider>
    </ErrorBoundary>
  );
}

export default App;